import React, { useEffect } from 'react';
import { Typography, Tooltip, Select, Spin } from 'antd';
import { QuestionCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import LabelExtractionManager from '@/features/datasets/components/LabelExtractionManager';
import EmptyResponsesChoice from '@/features/datasets/components/datasetUpload/EmptyResponsesChoice';
import HasLabelsSwitch from '@/features/datasets/components/datasetUpload/HasLabelsSwitch';
import NoLabelsModeChoice from '@/features/datasets/components/datasetUpload/NoLabelsModeChoice';
import AIGeneratedLabelsManager from '@/features/datasets/components/AIGeneratedLabelsManager';
import {
    NO_LABELS_MODE_DEFAULT,
    NO_LABELS_MODE_GENERATE_WITH_AI,
    NO_LABELS_MODE_UNLABELED,
} from '@/features/datasets/utils/hasLabelsHandling';
import { makeEmptyAiLabelRow } from '@/features/tasks/utils/aiLabels';
import { TaskType } from '@/shared/domain/taskTypes';
const { Text } = Typography;

const InlineColumnSelector = ({ label, value, onChange, options = [], placeholder = "Select column", loading = false, error, title, tooltipText }) => {
    const headingText = title || `Select ${label} column`;
    const tooltip = tooltipText || `Select the column that contains the ${label} for the examples`;
    const renderOption = (opt) => {
        if (typeof opt === 'string') return <Select.Option key={opt} value={opt}>{opt}</Select.Option>;
        const v = opt?.value ?? ''; const l = opt?.label ?? String(v);
        return <Select.Option key={v} value={v}>{l}</Select.Option>;
    };
    return (
        <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                <Text style={{ fontWeight: 400, color: '#222', fontSize: '14px', margin: 0 }}>{headingText}</Text>
                <Tooltip title={tooltip}>
                    <QuestionCircleOutlined style={{ color: '#878787', fontSize: '12px', cursor: 'help' }} />
                </Tooltip>
            </div>
            <Select value={value} onChange={onChange} placeholder={loading ? 'Loading...' : placeholder} loading={loading} disabled={loading} notFoundContent={loading ? <Spin size="small" /> : null} suffixIcon={loading ? <LoadingOutlined style={{ color: '#878787' }} spin /> : undefined} style={{ width: '100%' }} allowClear>
                {options.map(renderOption)}
            </Select>
            {error ? <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 6 }}>{error}</div> : null}
        </div>
    );
};

const seedEntityRows = (availableLabels = []) => {
    const unique = Array.from(
        new Set((availableLabels || []).map((l) => (l || '').trim()).filter(Boolean))
    );
    if (unique.length === 0) return [makeEmptyAiLabelRow(0)];
    return unique.map((name, idx) => ({
        id: `ai_entity_${Date.now()}_${idx}`,
        name,
        replaceWith: name,
        isNew: false,
    }));
};

/**
 * Per-objective form for Entity Extraction (NER) outputs, with optional
 * relations column when the objective is NER + relations.
 *
 * @param {Object} props
 * @param {Object} props.entry
 * @param {(patch: Object) => void} props.onPatch
 * @param {boolean} [props.withRelations] - True when task is NER_WITH_RELATIONS.
 */
const EntityOutputForm = ({
    entry,
    onPatch,
    columns,
    columnsLoading,
    withRelations,
    extractedLabels = [],
    labelsLoading,
    labelExamples = {},
    availableLabels = [],
    extractedRelations = [],
    relationsLoading,
    onDeleteLabel,
    onReplaceLabel,
    onAddNewLabel,
    onDeleteRelation,
    onReplaceRelation,
    onAddNewRelation,
    labelError,
}) => {
    const taskType = withRelations ? TaskType.NER_WITH_RELATIONS : TaskType.NER;
    const hasLabels = entry?.hasLabels !== false;
    const noLabelsMode = entry?.noLabelsMode || NO_LABELS_MODE_DEFAULT;
    const showAiCatalog =
        !hasLabels && noLabelsMode === NO_LABELS_MODE_GENERATE_WITH_AI;
    const aiLabels = entry?.aiLabels || [];

    // Seed entity catalog from task entities when entering generate-with-AI
    // with an empty list (suggest-labels is classification-only).
    useEffect(() => {
        if (!showAiCatalog) return;
        if ((aiLabels || []).length > 0) return;
        onPatch({ aiLabels: seedEntityRows(availableLabels) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showAiCatalog]);

    const updateRow = (id, patch) => {
        onPatch({
            aiLabels: (aiLabels || []).map((row) =>
                row.id === id ? { ...row, ...patch } : row
            ),
        });
    };

    return (
        <div>
            <HasLabelsSwitch
                taskType={taskType}
                checked={hasLabels}
                onChange={(checked) => {
                    if (checked) {
                        onPatch({
                            hasLabels: true,
                            noLabelsMode: NO_LABELS_MODE_DEFAULT,
                            aiLabels: [],
                        });
                    } else {
                        onPatch({
                            hasLabels: false,
                            noLabelsMode: NO_LABELS_MODE_DEFAULT,
                            labelColumn: null,
                            relationsColumn: null,
                            extractedLabels: [],
                            labelMappings: {},
                            labelExamples: {},
                        });
                    }
                }}
            />

            {!hasLabels ? (
                <>
                    <NoLabelsModeChoice
                        taskType={taskType}
                        value={noLabelsMode}
                        onChange={(mode) => {
                            onPatch({
                                noLabelsMode: mode,
                                ...(mode === NO_LABELS_MODE_UNLABELED
                                    ? { aiLabels: [] }
                                    : mode === NO_LABELS_MODE_GENERATE_WITH_AI &&
                                        (aiLabels || []).length === 0
                                      ? { aiLabels: seedEntityRows(availableLabels) }
                                      : {}),
                            });
                        }}
                    />
                    {showAiCatalog ? (
                        <div style={{ marginTop: 8 }}>
                            <AIGeneratedLabelsManager
                                title="Entity types for AI generation"
                                labels={aiLabels}
                                availableLabels={availableLabels}
                                onChangeNewLabelName={(id, nextName) =>
                                    updateRow(id, { name: nextName })
                                }
                                onChangeReplaceWith={(id, nextReplace) => {
                                    const row = (aiLabels || []).find((r) => r.id === id);
                                    const isNew = !!row?.isNew;
                                    updateRow(id, {
                                        replaceWith: nextReplace,
                                        name: isNew
                                            ? row?.name
                                            : row?.name
                                              ? row.name
                                              : nextReplace || '',
                                    });
                                }}
                                onDeleteLabel={(id) =>
                                    onPatch({
                                        aiLabels: (aiLabels || []).filter((r) => r.id !== id),
                                    })
                                }
                                onAddLabel={() =>
                                    onPatch({
                                        aiLabels: [
                                            ...(aiLabels || []),
                                            makeEmptyAiLabelRow((aiLabels || []).length),
                                        ],
                                    })
                                }
                            />
                        </div>
                    ) : null}
                </>
            ) : (
                <>
                    <InlineColumnSelector
                        label="entities"
                        title="Select entities field"
                        tooltipText="Select the column that contains the entities for the examples"
                        value={entry.labelColumn}
                        onChange={(v) => onPatch({ labelColumn: v })}
                        options={columns}
                        placeholder="Select entities column"
                        loading={columnsLoading}
                        error={labelError}
                    />
                    {withRelations && (
                        <InlineColumnSelector
                            label="relations"
                            title="Select relations field"
                            tooltipText="Select the column that contains the relations for the examples"
                            value={entry.relationsColumn}
                            onChange={(v) => onPatch({ relationsColumn: v })}
                            options={columns}
                            placeholder="Select relations column"
                            loading={columnsLoading}
                        />
                    )}
                    {entry.labelColumn && (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    marginBottom: 8,
                                }}
                            >
                                <Text style={{ fontWeight: 600, color: '#222', fontSize: 14 }}>
                                    Entities
                                </Text>
                                <Tooltip title="Review and normalize extracted entities. Hover an entity to see an example.">
                                    <QuestionCircleOutlined style={{ color: '#878787', fontSize: 14 }} />
                                </Tooltip>
                            </div>
                            <LabelExtractionManager
                                labelColumn={entry.labelColumn}
                                extractedLabels={extractedLabels}
                                loading={labelsLoading}
                                labelMappings={entry.labelMappings}
                                onDeleteLabel={onDeleteLabel}
                                onReplaceLabel={onReplaceLabel}
                                onAddNewLabel={onAddNewLabel}
                                labelType="Entity"
                                taskType={taskType}
                                labelExamples={labelExamples}
                                availableLabels={availableLabels}
                            />
                            <EmptyResponsesChoice
                                isMultilabel={false}
                                taskType={taskType}
                                value={entry.emptyMultilabelHandling || 'unlabeled'}
                                onChange={(v) => onPatch({ emptyMultilabelHandling: v })}
                            />
                        </div>
                    )}
                    {withRelations && entry.relationsColumn && (
                        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    marginBottom: 8,
                                }}
                            >
                                <Text style={{ fontWeight: 600, color: '#222', fontSize: 14 }}>
                                    Relations
                                </Text>
                                <Tooltip title="Review and normalize extracted relation types">
                                    <QuestionCircleOutlined style={{ color: '#878787', fontSize: 14 }} />
                                </Tooltip>
                            </div>
                            <LabelExtractionManager
                                labelColumn={entry.relationsColumn}
                                extractedLabels={extractedRelations}
                                loading={relationsLoading}
                                labelMappings={entry.relationsMappings}
                                onDeleteLabel={onDeleteRelation}
                                onReplaceLabel={onReplaceRelation}
                                onAddNewLabel={onAddNewRelation}
                                labelType="Relation"
                                taskType={TaskType.NER_WITH_RELATIONS}
                                loadingItemPluralLabel="relations"
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default EntityOutputForm;
